import { KDS_DEFAULTS } from '@didto/domain';
import type { Row } from './db';
import { supabase } from './supabase';

/**
 * Reads for the Kitchen and Bar display (Phase 5, ADR-020). One round trip per list: tickets with
 * their order header and their production items, each with the order line snapshot and modifiers.
 * Writes go through runCommand with an explicit retry (see routes/kds/kdsSupport.ts).
 */
export type TicketOrder = Pick<
  Row<'orders'>,
  | 'order_number'
  | 'type'
  | 'customer_name'
  | 'dining_table_id'
  | 'notes'
  | 'fulfilment_status'
  | 'scheduled_for'
>;
export type ItemLine = Pick<
  Row<'order_item'>,
  'name_snapshot' | 'variant_name_snapshot' | 'notes' | 'status' | 'seq'
> & { order_item_modifier: Pick<Row<'order_item_modifier'>, 'option_name_snapshot'>[] };
export type ProductionItem = Pick<
  Row<'production_item'>,
  | 'id'
  | 'ticket_id'
  | 'order_item_id'
  | 'status'
  | 'qty'
  | 'started_at'
  | 'first_started_at'
  | 'ready_at'
> & { order_item: ItemLine | null };
export type Ticket = Pick<
  Row<'production_ticket'>,
  | 'id'
  | 'order_id'
  | 'production_station_id'
  | 'ticket_seq'
  | 'is_addition'
  | 'status'
  | 'priority'
  | 'sent_at'
  | 'first_started_at'
  | 'started_at'
  | 'ready_at'
  | 'cancelled_at'
  | 'dismissed_at'
  | 'version'
> & { orders: TicketOrder | null; production_item: ProductionItem[] };

export type Station = Pick<
  Row<'production_station'>,
  | 'id'
  | 'code'
  | 'name'
  | 'color'
  | 'production_route_id'
  | 'warn_seconds'
  | 'late_seconds'
  | 'archived_at'
> & { production_route: { code: string } | null };

export const kdsKeys = {
  all: ['kds'] as const,
  queue: (stationId: string) => ['kds', 'queue', stationId] as const,
  recent: (stationId: string) => ['kds', 'recent', stationId] as const,
  cancelled: (stationId: string) => ['kds', 'cancelled', stationId] as const,
  station: (stationId: string) => ['kds', 'station', stationId] as const,
  stations: (locationId: string) => ['kds', 'stations', locationId] as const,
  /** Waiting ticket ids of a display's other tab (ADR-045): its own key, never a board's. */
  waiting: (stationIds: readonly string[]) =>
    ['kds', 'waiting', ...[...stationIds].sort()] as const,
};

const TICKET_SELECT = `id, order_id, production_station_id, ticket_seq, is_addition, status, priority, sent_at,
  first_started_at, started_at, ready_at, cancelled_at, dismissed_at, version,
  orders(order_number, type, customer_name, dining_table_id, notes, fulfilment_status, scheduled_for),
  production_item(id, ticket_id, order_item_id, status, qty, started_at, first_started_at, ready_at,
    order_item(name_snapshot, variant_name_snapshot, notes, status, seq, order_item_modifier(option_name_snapshot)))`;

const STATION_SELECT =
  'id, code, name, color, production_route_id, warn_seconds, late_seconds, archived_at, production_route(code)';

async function rows<T>(q: PromiseLike<{ data: unknown; error: unknown }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as T[];
}

/** Live tickets of a station: queued and in progress, oldest first (priority is applied by the caller). */
export const fetchStationQueue = (stationId: string) =>
  rows<Ticket>(
    supabase
      .from('production_ticket')
      .select(TICKET_SELECT)
      .eq('production_station_id', stationId)
      .in('status', ['queued', 'in_progress'])
      .order('sent_at'),
  );

/**
 * Ids of the tickets waiting (queued or in progress) at some stations: the count a Kitchen + Bar display shows for its
 * other tab, and the new ids that make it chime. Ids only: the other tab's tickets are never loaded into this board.
 */
export const fetchStationsWaiting = (stationIds: readonly string[]) =>
  rows<{ id: string; production_station_id: string }>(
    supabase
      .from('production_ticket')
      .select('id, production_station_id')
      .in('production_station_id', [...stationIds])
      .in('status', ['queued', 'in_progress'])
      .order('sent_at'),
  );

/** Bounded recall drawer: the last bumped tickets inside the recall window (owner refinement 6). */
export const fetchStationRecent = (stationId: string) =>
  rows<Ticket>(
    supabase
      .from('production_ticket')
      .select(TICKET_SELECT)
      .eq('production_station_id', stationId)
      .eq('status', 'ready')
      .gte(
        'ready_at',
        new Date(Date.now() - KDS_DEFAULTS.recallWindowMinutes * 60_000).toISOString(),
      )
      .order('ready_at', { ascending: false })
      .limit(KDS_DEFAULTS.recallLimit),
  );

/** Cancelled tickets nobody has dismissed yet (last 24 hours; older ones stay in the database). */
export const fetchStationCancelled = (stationId: string) =>
  rows<Ticket>(
    supabase
      .from('production_ticket')
      .select(TICKET_SELECT)
      .eq('production_station_id', stationId)
      .eq('status', 'cancelled')
      .is('dismissed_at', null)
      .gte('cancelled_at', new Date(Date.now() - 24 * 3_600_000).toISOString())
      .order('cancelled_at', { ascending: false }),
  );

export async function fetchStation(stationId: string): Promise<Station | null> {
  const { data, error } = await supabase
    .from('production_station')
    .select(STATION_SELECT)
    .eq('id', stationId)
    .maybeSingle();
  if (error) throw error;
  return data as unknown as Station | null;
}

/** Stations of a location with their route code (browser preview picks one; a device is bound). */
export const fetchStations = (locationId: string) =>
  rows<Station>(
    supabase
      .from('production_station')
      .select(STATION_SELECT)
      .eq('location_id', locationId)
      .is('archived_at', null)
      .order('sort'),
  );

/** Items of a ticket in order-line order. */
export function ticketItems(t: Ticket): ProductionItem[] {
  return [...t.production_item].sort(
    (a, b) => (a.order_item?.seq ?? 0) - (b.order_item?.seq ?? 0) || a.id.localeCompare(b.id),
  );
}
